import { Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { noShowRate } from '#lib/react-query/queries';
import { STAT_WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { Base } from '../Base.tsx';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '../InfoTemplateComponents.tsx';
import { useAppointmentWidget } from '../use-appointment-widget.ts';
import { StatBase } from './StatBase.tsx';

const widgetTitle = 'No-Show Rate';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Measures how often patients miss scheduled appointments without notice. High no-show rates
        mean lost revenue, wasted staff time, and fewer patients accessing care. Monitoring this
        metric helps identify and reduce avoidable gaps in service delivery and provider
        utilization.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection title="No-Show Rate =" indented>
        <InfoList
          items={[
            <>
              <InfoEmphasis>Numerator:</InfoEmphasis>
              &nbsp;Appointments marked as no-shows (not canceled or deleted)
            </>,
            <>
              <InfoEmphasis>Denominator:</InfoEmphasis>
              &nbsp;All appointments (not canceled or deleted)
            </>,
            'Uses the same filters you’ve applied (date range, location, provider, etc.)',
          ]}
        />
      </InfoSection>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Appointments marked as no-show',
            'Appointments within the applied date and filter criteria',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList items={['Canceled appointments', 'Deleted appointments']} />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            'No-show and canceled appointments are mutually exclusive in this metric, so appointments are never double-counted',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

export function NoShowRate() {
  const widget = useAppointmentWidget();

  const { data, isLoading } = useQuery(noShowRate(widget.getCommonRequestParams()));

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={STAT_WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <StatBase
      data={{
        id: 'no-show-rate',
        title: widgetTitle,
        value: data?.value ?? null,
        valueType: 'percentage',
        trend: data?.trend
          ? {
              ...data.trend,
              daysInPeriod: widget.homeLoaderData.totalDaysInRange,
            }
          : undefined,
      }}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    />
  );
}
